import { Container, Sprite, Texture } from "pixi.js";
import { NULL_LOG, type Log } from "../log/Log";
import { SpriteFontDefinition } from "./SpriteFontDefinition";

export enum SpriteTextAlignment {
  Left = 0,
  Center = 1,
  Right = 2,
}

export enum SpriteTextFitMode {
  None = 0,
  Width = 1,
  Height = 2,
  Both = 3,
}

export interface SpriteTextOptions {
  log?: Log;
  /** Maximum characters this renderer can display. Pre-allocates this many glyph sprites. */
  maxCharacters?: number;
  /** Horizontal text alignment relative to this node's position. */
  alignment?: SpriteTextAlignment;
  /** Uniform scale applied to all glyph sprites. */
  glyphScale?: number;
  /** Fit mode: shrink glyphs to fit `boxWidth`/`boxHeight`. Never modifies the node's own scale. */
  fitMode?: SpriteTextFitMode;
  /** The fit box (the UITransform contentSize). */
  boxWidth?: number;
  boxHeight?: number;
  font?: SpriteFontDefinition | null;
  label?: string;
}

/**
 * Renders a text string as individual sprite characters from a SpriteFontDefinition — the
 * `SpriteTextRenderer`. Designed for 60fps count-up animations: pre-allocated glyph pool,
 * character-level diffing, skip-relayout optimization for uniform-width digits.
 *
 * Usage: construct, `setFontDefinition(font)` once at composition time, then `setText("123.45")`.
 */
export class SpriteText extends Container {
  private readonly log: Log;
  public maxCharacters: number;
  public alignment: SpriteTextAlignment;
  public glyphScale: number;
  public fitMode: SpriteTextFitMode;
  public boxWidth: number;
  public boxHeight: number;

  private font: SpriteFontDefinition | null = null;
  private readonly glyphSprites: Sprite[] = [];
  private currentText: string = "";
  private currentChars: string[] = [];
  private activeCount: number = 0;
  private readonly warnedChars: Set<string> = new Set();
  private pendingText: string | null = null;

  private uniformDigitWidth: number = -1; // -1 = not uniform or not checked
  private lastTotalWidth: number = 0;
  private lastFitFactor: number = 1;

  public constructor(options: SpriteTextOptions = {}) {
    super({ label: options.label ?? "SpriteText" });
    this.log = options.log ?? NULL_LOG;
    this.maxCharacters = options.maxCharacters ?? 24;
    this.alignment = options.alignment ?? SpriteTextAlignment.Center;
    this.glyphScale = options.glyphScale ?? 1;
    this.fitMode = options.fitMode ?? SpriteTextFitMode.None;
    this.boxWidth = options.boxWidth ?? 0;
    this.boxHeight = options.boxHeight ?? 0;
    this.ensureGlyphPool();
    if (options.font) this.setFontDefinition(options.font);
  }

  // ──────────────────────────────────────────────
  //  Public API
  // ──────────────────────────────────────────────

  /** Bind a pre-built SpriteFontDefinition. Called once during composition, before any setText. */
  public setFontDefinition(font: SpriteFontDefinition): void {
    this.font = font;
    this.uniformDigitWidth = this.computeUniformDigitWidth(font);

    if (this.pendingText !== null) {
      const text = this.pendingText;
      this.pendingText = null;
      this.setText(text);
    }
  }

  /**
   * Set the displayed text. This is the hot path for count-up animations.
   * Zero allocation per call. Character-level diff minimizes sprite writes.
   */
  public setText(text: string): void {
    if (text === this.currentText) return;
    if (!this.font) {
      this.pendingText = text;
      return;
    }
    this.pendingText = null;

    const font = this.font;
    const prevChars = this.currentChars;
    const prevLen = prevChars.length;
    const nextLen = text.length;
    const maxLen = Math.min(nextLen, this.maxCharacters);

    let needsRelayout = nextLen !== prevLen;

    for (let i = 0; i < maxLen; i++) {
      const char = text[i];
      const sprite = this.glyphSprites[i];

      if (char === " ") {
        if (i < prevLen && prevChars[i] === " ") continue; // same space
        sprite.visible = false;
        needsRelayout = true;
        continue;
      }

      const glyph = font.getGlyph(char);
      if (!glyph) {
        sprite.visible = false;
        if (!this.warnedChars.has(char)) {
          this.warnedChars.add(char);
          this.log(`[SpriteText] missing glyph for char="${char}"`);
        }
        needsRelayout = true;
        continue;
      }

      if (i >= prevLen || char !== prevChars[i]) {
        sprite.texture = glyph.texture;

        if (i < prevLen && prevChars[i] !== " ") {
          const prevGlyph = font.getGlyph(prevChars[i]);
          if (prevGlyph && prevGlyph.advanceWidth !== glyph.advanceWidth) {
            needsRelayout = true;
          }
        } else {
          needsRelayout = true;
        }
      }

      if (!sprite.visible) sprite.visible = true;
    }

    for (let i = maxLen; i < this.activeCount; i++) {
      this.glyphSprites[i].visible = false;
    }

    this.activeCount = maxLen;
    this.currentText = text;
    this.currentChars = text.split("");

    if (needsRelayout || this.uniformDigitWidth < 0 || this.fitMode !== SpriteTextFitMode.None) {
      this.layoutGlyphs();
    }
  }

  public getText(): string {
    return this.currentText;
  }

  /** Measured content width of the current text in pixels (before the fit factor). */
  public getContentWidth(): number {
    return this.lastTotalWidth;
  }

  public getFitFactor(): number {
    return this.lastFitFactor;
  }

  /**
   * The active glyph sprites (the visible characters), for effects that drive a per-glyph
   * filter — e.g. a shine sweep. Don't retain them across a `setText()`.
   */
  public getActiveGlyphSprites(): Sprite[] {
    const out: Sprite[] = [];
    for (let i = 0; i < this.activeCount; i++) {
      if (this.glyphSprites[i].visible) out.push(this.glyphSprites[i]);
    }
    return out;
  }

  public clearText(): void {
    this.setText("");
  }

  // ──────────────────────────────────────────────
  //  Internals
  // ──────────────────────────────────────────────

  private ensureGlyphPool(): void {
    for (let i = this.glyphSprites.length; i < this.maxCharacters; i++) {
      const sprite = new Sprite({ texture: Texture.EMPTY, anchor: 0.5, label: `_g${i}`, visible: false });
      if (this.glyphScale !== 1) sprite.scale.set(this.glyphScale);
      this.addChild(sprite);
      this.glyphSprites.push(sprite);
    }
  }

  private layoutGlyphs(): void {
    if (!this.font) return;

    const font = this.font;
    const chars = this.currentChars;
    const count = Math.min(chars.length, this.maxCharacters);
    const spacing = font.letterSpacing;
    const scale = this.glyphScale;

    // --- Measure pass (natural size using glyphScale) ---
    let totalWidth = 0;
    let maxHeight = 0;
    const advances: number[] = [];

    for (let i = 0; i < count; i++) {
      const char = chars[i];
      let advance: number;

      if (char === " ") {
        advance = font.spaceWidth;
      } else {
        const glyph = font.getGlyph(char);
        if (!glyph) {
          advance = 0;
        } else {
          advance = glyph.advanceWidth;
          if (glyph.height > maxHeight) maxHeight = glyph.height;
        }
      }

      advances.push(advance);
      totalWidth += advance * scale;
      if (i < count - 1 && advance > 0) {
        totalWidth += spacing * scale;
      }
    }

    this.lastTotalWidth = totalWidth;

    // --- Fit factor computation (shrink-only, never enlarge) ---
    let fitFactor = 1;
    if (this.fitMode !== SpriteTextFitMode.None) {
      const boxW = this.boxWidth;
      const boxH = this.boxHeight;

      if (this.fitMode === SpriteTextFitMode.Width) {
        if (totalWidth > boxW && totalWidth > 0 && boxW > 0) fitFactor = boxW / totalWidth;
      } else if (this.fitMode === SpriteTextFitMode.Height) {
        const totalHeight = maxHeight * scale;
        if (totalHeight > boxH && totalHeight > 0 && boxH > 0) fitFactor = boxH / totalHeight;
      } else if (this.fitMode === SpriteTextFitMode.Both) {
        let fw = 1;
        let fh = 1;
        if (totalWidth > boxW && totalWidth > 0 && boxW > 0) fw = boxW / totalWidth;
        const totalHeight = maxHeight * scale;
        if (totalHeight > boxH && totalHeight > 0 && boxH > 0) fh = boxH / totalHeight;
        fitFactor = Math.min(fw, fh);
      }

      if (fitFactor > 1) fitFactor = 1;
    }
    this.lastFitFactor = fitFactor;

    // --- Position pass (effectiveScale = glyphScale × fitFactor) ---
    const effectiveScale = scale * fitFactor;
    const fittedWidth = totalWidth * fitFactor;

    let startX: number;
    switch (this.alignment) {
      case SpriteTextAlignment.Left:
        startX = 0;
        break;
      case SpriteTextAlignment.Right:
        startX = -fittedWidth;
        break;
      case SpriteTextAlignment.Center:
      default:
        startX = -fittedWidth / 2;
        break;
    }

    let cursor = 0;
    for (let i = 0; i < count; i++) {
      const advance = advances[i];
      const char = chars[i];

      if (char === " " || advance === 0) {
        cursor += advance * effectiveScale;
        if (i < count - 1 && advance > 0) cursor += spacing * effectiveScale;
        continue;
      }

      const glyph = font.getGlyph(char);
      // Glyph offsets are authored y-up (font definition format): offsetY raises the glyph; Pixi is y-down.
      const baselineShift = glyph ? ((glyph.height - maxHeight) / 2) * effectiveScale : 0;
      const offsetY = (glyph ? glyph.offsetY * effectiveScale : 0) + baselineShift;
      const x = startX + cursor + (advance * effectiveScale) / 2;

      this.glyphSprites[i].position.set(x, -offsetY);
      this.glyphSprites[i].scale.set(effectiveScale);
      cursor += advance * effectiveScale;
      if (i < count - 1) cursor += spacing * effectiveScale;
    }
  }

  /** Check if all digit glyphs (0-9) share the same advance width (count-ups skip relayout). */
  private computeUniformDigitWidth(font: SpriteFontDefinition): number {
    let width = -1;
    for (let d = 0; d <= 9; d++) {
      const glyph = font.getGlyph(d.toString());
      if (!glyph) return -1;
      if (width < 0) {
        width = glyph.advanceWidth;
      } else if (glyph.advanceWidth !== width) {
        return -1;
      }
    }
    return width;
  }
}
